// Inactivity window: the timer is reset on every activity/content frame received
// from the backend (see `bump`), so a healthy run that keeps streaming progress
// (e.g. a loop over many items) never trips this even past 120s of wall time.
export const WORKFLOW_TEST_RUN_TIMEOUT_MS = 120_000
// Absolute ceiling regardless of activity, so a run that streams forever (e.g. a
// runaway loop) still gets cut off eventually.
export const WORKFLOW_TEST_RUN_MAX_DURATION_MS = 30 * 60_000
export const WORKFLOW_TEST_RUN_TIMEOUT_MESSAGE = "Workflow test run timed out"

export function createWorkflowTestRunTimeout(
  abortController: AbortController,
  timeoutMs = WORKFLOW_TEST_RUN_TIMEOUT_MS,
  maxDurationMs = WORKFLOW_TEST_RUN_MAX_DURATION_MS
) {
  let timedOut = false
  let inactivityTimeoutId: ReturnType<typeof setTimeout> | undefined
  let maxDurationTimeoutId: ReturnType<typeof setTimeout> | undefined
  let rejectFn: ((error: Error) => void) | undefined

  const fireTimeout = () => {
    timedOut = true
    abortController.abort()
    rejectFn?.(new Error(WORKFLOW_TEST_RUN_TIMEOUT_MESSAGE))
  }

  const promise = new Promise<never>((_, reject) => {
    rejectFn = reject
    inactivityTimeoutId = setTimeout(fireTimeout, timeoutMs)
    maxDurationTimeoutId = setTimeout(fireTimeout, maxDurationMs)
  })

  const clearInactivityTimer = () => {
    if (inactivityTimeoutId !== undefined) {
      clearTimeout(inactivityTimeoutId)
      inactivityTimeoutId = undefined
    }
  }

  const clearMaxDurationTimer = () => {
    if (maxDurationTimeoutId !== undefined) {
      clearTimeout(maxDurationTimeoutId)
      maxDurationTimeoutId = undefined
    }
  }

  return {
    promise,
    timedOut: () => timedOut,
    /**
     * Reset the inactivity window. Call this on every incoming activity/content
     * frame so a run that's actively streaming progress never hits the
     * inactivity timeout, even if its total wall time exceeds `timeoutMs`.
     * No-op once the timeout has already fired or `clear()` was called.
     */
    bump: () => {
      if (timedOut || inactivityTimeoutId === undefined) return
      clearInactivityTimer()
      inactivityTimeoutId = setTimeout(fireTimeout, timeoutMs)
    },
    clear: () => {
      clearInactivityTimer()
      clearMaxDurationTimer()
    },
  }
}
